import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Filter, X } from 'lucide-react';

interface FiltersBarProps {
  isOpen: boolean;
  onToggle: () => void;
  filters: {
    sdr: string;
    client: string;
    status: string;
  };
  onFilterChange: (filterType: 'sdr' | 'client' | 'status', value: string) => void;
  sdrOptions: string[];
  clientOptions: string[];
  statusOptions: string[];
}

const FiltersBar: React.FC<FiltersBarProps> = ({
  isOpen,
  onToggle,
  filters,
  onFilterChange,
  sdrOptions,
  clientOptions,
  statusOptions
}) => {
  return (
    <div className="mb-6">
      <motion.button
        onClick={onToggle}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-xl hover:border-cyan-300 hover:bg-cyan-50 transition-all shadow-sm"
      >
        <Filter className="h-4 w-4 text-gray-600" />
        <span className="text-sm font-medium text-gray-700">Filtres</span>
        {(filters.sdr !== 'all' || filters.client !== 'all' || filters.status !== 'all') && (
          <span className="ml-2 inline-flex items-center justify-center h-5 w-5 rounded-full bg-cyan-500 text-white text-xs font-bold">
            {[filters.sdr, filters.client, filters.status].filter((f) => f !== 'all').length}
          </span>
        )}
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="mt-4 bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900">Filtrer les résultats</h3>
                <button
                  onClick={onToggle}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">SDR</label>
                  <select
                    value={filters.sdr}
                    onChange={(e) => onFilterChange('sdr', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                  >
                    <option value="all">Tous les SDRs</option>
                    {sdrOptions.map((sdr) => (
                      <option key={sdr} value={sdr}>
                        {sdr}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Client</label>
                  <select
                    value={filters.client}
                    onChange={(e) => onFilterChange('client', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                  >
                    <option value="all">Tous les clients</option>
                    {clientOptions.map((client) => (
                      <option key={client} value={client}>
                        {client}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">Statut</label>
                  <select
                    value={filters.status}
                    onChange={(e) => onFilterChange('status', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                  >
                    <option value="all">Tous les statuts</option>
                    {statusOptions.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {(filters.sdr !== 'all' || filters.client !== 'all' || filters.status !== 'all') && (
                <motion.button
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  onClick={() => {
                    onFilterChange('sdr', 'all');
                    onFilterChange('client', 'all');
                    onFilterChange('status', 'all');
                  }}
                  className="mt-4 text-sm text-cyan-600 hover:text-cyan-700 font-medium flex items-center space-x-1"
                >
                  <X className="h-3 w-3" />
                  <span>Réinitialiser les filtres</span>
                </motion.button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FiltersBar;
